import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  createAdminTenancyNote,
  fetchAdminTenancyNotes,
  updateAdminTenancyNote,
} from "../../../api/adminData/tenancies";
import { formatDateTime } from "../../../utils/datetime";
import {
  Card,
  EmptyState,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from "./workspaceUi";

/**
 * Internal notes on ONE tenancy.
 *
 * NOT TENANT NOTES, and there is no fallback to them. A tenant can hold
 * several tenancies — historical, future, parallel, and back-to-back in the
 * same room — so a note kept on the person cannot say which contract it
 * belongs to, and `tenant_notes` has no `tenancy_id` to recover it from.
 * Every call here goes to `/tenancies/{id}/notes`, and the person-level
 * clients are not imported (pinned by `tenancyNotesGuards.test.js`).
 *
 * ONE REQUEST, ONLY WHEN OPENED. The fetch is gated on `active`, so a tenancy
 * the reader only glanced at costs nothing, and selecting a row costs nothing
 * either. The Übersicht card deliberately shows no count: a count is a request
 * per row click for a number that is one click away.
 *
 * ALL STATE BELONGS TO ONE TENANCY. `TenancyDetailContent` is not keyed per
 * tenancy, so it does NOT remount when another row is selected and this tab
 * can stay open across the switch. Everything below therefore resets on a
 * tenancy change — not just the list, but the composer, its draft, the edit
 * session and every error and in-flight flag. A half-written note for one
 * contract must not reappear under another, and an edit session for a note of
 * A must not be saveable while B is on screen.
 *
 * LATE RESPONSES BELONG TO THE TENANCY THAT ASKED. Reads AND writes capture
 * the tenancy id at call time and drop their result if the selection has
 * moved on (`currentIdRef`). Without that, a slow POST for A would insert A's
 * note into B's list, or close B's composer.
 *
 * NO DELETE: the backend has none, and neither does any other notes domain in
 * this product.
 */

/** The tenancy whose notes are shown; `null` while nothing is loaded. */
const EMPTY_NOTES = { loading: false, error: null, items: [] };

/** Composer + edit state, as one resettable unit. */
const EMPTY_EDITOR = {
  composerOpen: false,
  draft: "",
  creating: false,
  createError: null,
  editingId: null,
  editDraft: "",
  updating: false,
  editError: null,
};

/**
 * Who wrote it, and when it was last touched.
 *
 * STACKED BEFORE IT WRAPS. As one `flex-wrap` row the edited half carried its
 * own leading separator, so in the mobile sheet it wrapped to a line beginning
 * with a stray "·". Below `sm` the two halves are their own lines and the
 * separator is not rendered at all; from `sm` up they share a row with the
 * separator between them. The sheet only exists below `lg`, so the desktop
 * column always gets the row.
 */
function NoteMeta({ note }) {
  return (
    <div className="mt-[6px] flex flex-col gap-y-[1px] text-[11.5px] leading-snug text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-[6px]">
      <span>
        {note.author_name || "—"} · {formatDateTime(note.created_at)}
      </span>
      {/* Only once an edit actually happened. The server sends `editor_name`
          exclusively when `updated_at` is set, and nothing here invents it
          from the current user. */}
      {note.updated_at ? (
        <>
          <span aria-hidden="true" className="hidden sm:inline">
            ·
          </span>
          <span data-testid={`tenancy-note-edited-${note.id}`}>
            Bearbeitet von {note.editor_name || "—"} ·{" "}
            {formatDateTime(note.updated_at)}
          </span>
        </>
      ) : null}
    </div>
  );
}

export default function TenancyNotesTab({ tenancyId, active }) {
  const [notes, setNotes] = useState(EMPTY_NOTES);
  const [editor, setEditor] = useState(EMPTY_EDITOR);

  /**
   * The tenancy currently on screen, readable from any async callback.
   *
   * Assigned during render so it is never a commit behind: an in-flight
   * request started for A must be able to see, the moment it resolves, that
   * the selection has already moved to B.
   */
  const currentIdRef = useRef(tenancyId);
  currentIdRef.current = tenancyId;

  /** The tenancy whose notes are loaded or loading — guards a refetch on
   *  every tab switch, and marks the state as belonging to that tenancy. */
  const loadedFor = useRef(null);

  const load = useCallback((id) => {
    loadedFor.current = id;
    setNotes({ loading: true, error: null, items: [] });
    fetchAdminTenancyNotes(id)
      .then((d) => {
        if (currentIdRef.current !== id) return;
        setNotes({
          loading: false,
          error: null,
          items: Array.isArray(d?.items) ? d.items : [],
        });
      })
      .catch((e) => {
        if (currentIdRef.current !== id) return;
        setNotes({
          loading: false,
          error: e?.message || "Notizen konnten nicht geladen werden.",
          items: [],
        });
      });
  }, []);

  // A different tenancy — reset EVERYTHING this tab holds before anything
  // else runs. Declared before the loading effect so it wins the same commit:
  // it clears `loadedFor`, and the effect below then loads the new tenancy.
  useEffect(() => {
    if (loadedFor.current !== null && loadedFor.current !== tenancyId) {
      loadedFor.current = null;
      setNotes(EMPTY_NOTES);
      setEditor(EMPTY_EDITOR);
    }
  }, [tenancyId]);

  useEffect(() => {
    if (!active || !tenancyId) return;
    if (loadedFor.current === tenancyId) return;
    load(tenancyId);
  }, [active, tenancyId, load]);

  const retry = useCallback(() => {
    if (tenancyId) load(tenancyId);
  }, [tenancyId, load]);

  // ── Create ────────────────────────────────────────────────────────
  const submitCreate = useCallback(() => {
    const id = tenancyId;
    const content = editor.draft.trim();
    if (!id || !content) return;
    setEditor((s) => ({ ...s, creating: true, createError: null }));
    createAdminTenancyNote(id, content)
      .then((note) => {
        // The selection moved on while this was in flight: A's note must not
        // land in B's list, and B's composer must not be closed by it.
        if (currentIdRef.current !== id) return;
        setNotes((s) => ({ ...s, items: [note, ...s.items] }));
        setEditor(EMPTY_EDITOR);
      })
      .catch((e) => {
        if (currentIdRef.current !== id) return;
        setEditor((s) => ({
          ...s,
          creating: false,
          createError: e?.message || "Notiz konnte nicht gespeichert werden.",
        }));
      });
  }, [tenancyId, editor.draft]);

  // ── Edit ──────────────────────────────────────────────────────────
  const submitEdit = useCallback(() => {
    const id = tenancyId;
    const noteId = editor.editingId;
    const content = editor.editDraft.trim();
    if (!id || !noteId || !content) return;
    setEditor((s) => ({ ...s, updating: true, editError: null }));
    updateAdminTenancyNote(id, noteId, content)
      .then((updated) => {
        if (currentIdRef.current !== id) return;
        setNotes((s) => ({
          ...s,
          items: s.items.map((n) => (n.id === updated.id ? updated : n)),
        }));
        setEditor(EMPTY_EDITOR);
      })
      .catch((e) => {
        if (currentIdRef.current !== id) return;
        // The edit stays open with the user's text — a failed save must not
        // discard what they wrote.
        setEditor((s) => ({
          ...s,
          updating: false,
          editError: e?.message || "Notiz konnte nicht gespeichert werden.",
        }));
      });
  }, [tenancyId, editor.editingId, editor.editDraft]);

  // ── Render ────────────────────────────────────────────────────────

  const composerBusy = editor.creating;
  const canSaveNew = editor.draft.trim().length > 0 && !composerBusy;

  return (
    <div className="flex flex-col gap-3" data-testid="tenancy-notes">
      <Card
        title="Notizen"
        action={
          !editor.composerOpen ? (
            <button
              type="button"
              onClick={() =>
                setEditor((s) => ({ ...s, composerOpen: true, createError: null }))
              }
              className={SECONDARY_BUTTON}
            >
              + Notiz
            </button>
          ) : null
        }
      >
        <>
          {/* Composer FIRST, above the list: on a tenancy with a dozen notes
              you would otherwise scroll past all of them to write one. That is
              the defect `notesUx1.characterization` records as fixed. */}
          {editor.composerOpen ? (
            <form
              className="mb-4"
              data-testid="tenancy-note-composer"
              onSubmit={(e) => {
                e.preventDefault();
                submitCreate();
              }}
            >
              <label htmlFor="tenancy-note-draft" className="sr-only">
                Neue Notiz
              </label>
              <textarea
                id="tenancy-note-draft"
                value={editor.draft}
                onChange={(e) =>
                  setEditor((s) => ({ ...s, draft: e.target.value }))
                }
                disabled={composerBusy}
                placeholder="Neue Notiz verfassen …"
                className="min-h-[68px] max-h-[220px] w-full max-w-full rounded-[7px] border border-border bg-background px-3 py-2 text-[13px] leading-snug text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              {editor.createError ? (
                <p
                  role="alert"
                  data-testid="tenancy-note-create-error"
                  className="m-0 mt-2 text-[12.5px] text-rose-700 dark:text-rose-300"
                >
                  {editor.createError}
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditor(EMPTY_EDITOR)}
                  disabled={composerBusy}
                  className={SECONDARY_BUTTON}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={!canSaveNew}
                  className={PRIMARY_BUTTON}
                >
                  {composerBusy ? "Speichern …" : "Speichern"}
                </button>
              </div>
            </form>
          ) : null}

          {notes.loading ? (
            <div
              className="flex flex-col gap-2"
              aria-busy="true"
              data-testid="tenancy-notes-loading"
            >
              <span className="sr-only">Notizen werden geladen …</span>
              {[0, 1].map((i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  className="h-[58px] animate-pulse rounded-[7px] border border-border bg-muted/40"
                />
              ))}
            </div>
          ) : notes.error ? (
            <div
              role="alert"
              data-testid="tenancy-notes-error"
              className="text-[12.5px] text-rose-700 dark:text-rose-300"
            >
              <p className="m-0">{notes.error}</p>
              <button
                type="button"
                onClick={retry}
                className={`mt-2 ${SECONDARY_BUTTON}`}
              >
                Erneut versuchen
              </button>
            </div>
          ) : notes.items.length === 0 ? (
            <EmptyState
              testId="tenancy-notes-empty"
              title="Noch keine Notizen vorhanden."
              hint="Halte wichtige Informationen und Vereinbarungen zu diesem Mietverhältnis hier fest."
            />
          ) : (
            <ul className="m-0 flex list-none flex-col p-0">
              {notes.items.map((note) => {
                const isEditing = editor.editingId === note.id;
                return (
                  <li
                    key={note.id}
                    data-testid={`tenancy-note-${note.id}`}
                    className="border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0"
                  >
                    {isEditing ? (
                      <form
                        data-testid="tenancy-note-edit-form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          submitEdit();
                        }}
                      >
                        <label htmlFor={`tenancy-note-edit-${note.id}`} className="sr-only">
                          Notiz bearbeiten
                        </label>
                        <textarea
                          id={`tenancy-note-edit-${note.id}`}
                          value={editor.editDraft}
                          onChange={(e) =>
                            setEditor((s) => ({ ...s, editDraft: e.target.value }))
                          }
                          disabled={editor.updating}
                          className="min-h-[68px] max-h-[220px] w-full max-w-full rounded-[7px] border border-border bg-background px-3 py-2 text-[13px] leading-snug text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                        />
                        {editor.editError ? (
                          <p
                            role="alert"
                            data-testid="tenancy-note-edit-error"
                            className="m-0 mt-2 text-[12.5px] text-rose-700 dark:text-rose-300"
                          >
                            {editor.editError}
                          </p>
                        ) : null}
                        <div className="mt-2 flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setEditor((s) => ({
                                ...s,
                                editingId: null,
                                editDraft: "",
                                editError: null,
                                updating: false,
                              }))
                            }
                            disabled={editor.updating}
                            className={SECONDARY_BUTTON}
                          >
                            Abbrechen
                          </button>
                          <button
                            type="submit"
                            disabled={
                              editor.updating || editor.editDraft.trim().length === 0
                            }
                            className={PRIMARY_BUTTON}
                          >
                            {editor.updating ? "Speichern …" : "Speichern"}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                          <p className="m-0 min-w-0 flex-1 whitespace-pre-wrap break-words text-[13px] text-foreground">
                            {note.content}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              setEditor((s) => ({
                                ...s,
                                editingId: note.id,
                                editDraft: note.content || "",
                                editError: null,
                                updating: false,
                              }))
                            }
                            className={SECONDARY_BUTTON}
                          >
                            Bearbeiten
                          </button>
                        </div>
                        <NoteMeta note={note} />
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      </Card>
    </div>
  );
}
